import { useState, useCallback, useMemo } from 'react';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qSymptomsByEncounter,
  qSymptomSuggestions,
  type PatientSymptomEntryRow,
  type VttSuggestionRow,
} from '@helios/data-access';
import { ChipListField, type Chip } from './ChipListField';

/* ------------------------------------------------------------------ */
/*  Symptoms structured entry (PRD.214 Phase 1)                       */
/* ------------------------------------------------------------------ */

interface Props {
  patientId: string;
  encounterId: string;
  /** When true, hide the internal "Symptoms" header (used inside guided-note cards). */
  hideHeader?: boolean;
}

export function EncounterSymptomsTab({ patientId, encounterId, hideHeader }: Props) {
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';

  const { data: entries = [] } = usePowerSyncQuery<PatientSymptomEntryRow>(
    qSymptomsByEncounter(encounterId),
  );

  // Suggestions — derived from past usage, refreshed as the user types
  const [search, setSearch] = useState('');
  const { data: suggestionRows = [] } = usePowerSyncQuery<VttSuggestionRow>(
    qSymptomSuggestions(orgId, search),
  );
  const suggestions = useMemo(() => suggestionRows.map((r) => r.label), [suggestionRows]);

  const chips: Chip[] = useMemo(
    () =>
      entries.map((e) => ({
        id: e.id,
        label: e.label,
        // PRD.217 — source='ai' rows were auto-added by the dermatology AI
        // run and get an inline "AI Suggested" badge for clinical clarity.
        badge: e.source === 'ai' ? 'AI Suggested' : undefined,
      })),
    [entries],
  );

  const handleAdd = useCallback(
    async (label: string) => {
      if (!db) return;
      const now = new Date().toISOString();
      await db.execute(
        `INSERT INTO patient_symptom_entries
          (id, org_id, clinic_id, patient_id, encounter_id, label, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
        [crypto.randomUUID(), orgId, null, patientId, encounterId, label, now, now],
      );
    },
    [db, orgId, patientId, encounterId],
  );

  const handleRemove = useCallback(
    async (chip: Chip) => {
      if (!db) return;
      await db.execute(`DELETE FROM patient_symptom_entries WHERE id = ?`, [chip.id]);
    },
    [db],
  );

  return (
    <div
      style={{
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
      }}
    >
      {!hideHeader && (
        <div>
          <h2
            style={{
              margin: 0,
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            Symptoms
          </h2>
          <p
            style={{
              margin: '4px 0 0',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
            }}
          >
            What is the patient experiencing?
          </p>
        </div>
      )}
      <ChipListField
        chips={chips}
        suggestions={suggestions}
        onSearchChange={setSearch}
        onAdd={handleAdd}
        onRemove={handleRemove}
        subtitle="Add or edit the symptoms identified"
        addLabel="Add Symptom"
        placeholder="Add or edit symptoms…"
      />
    </div>
  );
}
